import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'

import { Icon } from '../Icon'
import Image from '../Image'

const BACKGROUND_GRADIENT =
  'radial-gradient(100% 7106.9% at 4.49% 0,hsla(0,0%,100%,.035) 0,#ffffff36 100%)'

type CompetitorComparisonProps = {
  data: {
    title: string
    company_logos: { _key: string; image: { asset: { url: string } } }[]
    group_items: {
      group_items: {
        _key: string
        group_name: string
        table_data: { rows: { _key: string; cells: string[] }[] }
      }[]
    }
  }
}

export const CompetitorComparison = ({ data }: CompetitorComparisonProps) => {
  const { palette } = useTheme()

  const renderCell = (cell: string) => {
    if (cell === 'CHECK') {
      return (
        <Icon
          icon='check'
          color={palette.text.primary}
          sx={{ svg: { height: { xs: 20, mobile: 28, laptop: 32 } } }}
        />
      )
    }

    if (!cell) {
      return (
        <Icon
          icon='minus'
          color={palette.text.primary}
          sx={{ svg: { height: { xs: 20, mobile: 28, laptop: 32 } } }}
        />
      )
    }

    return (
      <Typography
        sx={{ fontSize: { xs: 14, mobile: 18 }, textAlign: 'center' }}>
        {cell}
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        maxWidth: 1152,
        mx: 'auto',
        pt: { xs: 15, laptop: 18 }
      }}>
      <Typography
        variant='h1'
        sx={{ fontSize: { xs: 24, laptop: 54 }, px: 3, textAlign: 'center' }}>
        {data.title}
      </Typography>

      <Box
        sx={{
          width: { laptop: '66.66%' },
          height: 80,
          bgcolor: 'background.default',
          borderRadius: { laptop: 4 },
          ml: { laptop: '33.33%' },
          position: 'sticky',
          top: { xs: 68, laptop: 88 }
        }}
      />

      <Box
        sx={{
          width: { laptop: '66.66%' },
          background: BACKGROUND_GRADIENT,
          borderRadius: { laptop: 4 },
          display: 'flex',
          ml: { laptop: '33.33%' },
          mt: 5.5,
          position: 'sticky',
          top: { xs: 68, laptop: 88 },
          zIndex: 1
        }}>
        {data.company_logos.map(item => (
          <Box
            key={item._key}
            sx={{
              height: 80,
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              img: { maxWidth: { xs: 120, mobile: 135, laptop: '100%' } }
            }}>
            <Image url={item.image.asset?.url} />
          </Box>
        ))}
      </Box>

      {data.group_items.group_items.map(item => (
        <Box key={item._key} sx={{ mt: { xs: 2, laptop: 3 } }}>
          <Typography
            variant='h3'
            sx={{
              color: 'secondary.main',
              fontSize: { xs: 18, laptop: 24 },
              px: { xs: 1, laptop: 4 },
              py: { xs: 3, laptop: 4 },
              textAlign: { xs: 'center', laptop: 'left' }
            }}>
            {item.group_name.toUpperCase()}
          </Typography>

          {item.table_data.rows.map((row, rowIndex) => (
            <Box
              key={row._key}
              sx={{
                background: rowIndex % 2 === 0 ? BACKGROUND_GRADIENT : 'none',
                borderRadius: { laptop: 4 },
                display: { laptop: 'flex' },
                pl: { laptop: 3 },
                py: { xs: 2.5, laptop: 3.5 }
              }}>
              {row.cells[0] && (
                <Typography
                  sx={{
                    width: { laptop: '33.33%' },
                    fontSize: { xs: 16, mobile: 18 },
                    px: { xs: 1, laptop: 3.5 },
                    textAlign: { xs: 'center', laptop: 'left' }
                  }}>
                  {row.cells[0]}
                </Typography>
              )}

              <Box
                sx={{
                  width: { laptop: '66.66%' },
                  alignItems: 'center',
                  display: 'flex',
                  mt: { xs: 1.5, laptop: 0 }
                }}>
                <Box
                  sx={{
                    width: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    px: 1
                  }}>
                  {renderCell(row.cells[1])}
                </Box>
                <Box
                  sx={{
                    width: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    px: 1
                  }}>
                  {renderCell(row.cells[2])}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  )
}
